import React, { useEffect, useState } from 'react';

// Hooks
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Actions
import { fetchWebhookLogDetail } from '~/redux/actions/webhooks-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { getEvent, isSlackWebhook } from '~/utils/helperFunctions';

// Components
import JsonRenderer from './JsonRenderer';
import Loader from '~/components/General/Loader';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';

// MUI Components
import { Box, GlobalStyles, Typography } from '@mui/material';

// Styles
import './styles.scss';

const parseJsonValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const WebhookLog = () => {
  const { id, key } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigatedWebhookLog =
    location.state?.webhookLog && String(location.state.webhookLog.id) === String(id)
      ? location.state.webhookLog
      : null;

  const [selectedWebhookLog, setSelectedWebhookLog] = useState(navigatedWebhookLog);
  const [loading, setLoading] = useState(!navigatedWebhookLog);

  const getEventWithTestFlag = (log) => {
    const eventName = getEvent(log);
    const requestBody = parseJsonValue(log?.value?.request?.body);
    const isTest = requestBody?.isTest || false;

    return isTest ? `${eventName} [test]` : eventName;
  };

  const eventTitle = getEventWithTestFlag(selectedWebhookLog) || ' -';
  const statusCode =
    selectedWebhookLog?.responseStatusCode || selectedWebhookLog?.value?.response?.status || ' -';
  const formattedDate = dateFormat(selectedWebhookLog?.createdAt) || ' -';

  const parsedRequestBody = parseJsonValue(selectedWebhookLog?.value?.request?.body);
  const requestJson =
    isSlackWebhook(selectedWebhookLog?.value?.request?.url) && parsedRequestBody?.text
      ? parseJsonValue(parsedRequestBody.text)
      : parsedRequestBody;
  const responseJson = parseJsonValue(selectedWebhookLog?.value?.response?.data);

  useEffect(() => {
    let isActive = true;

    const fetchWebhookLog = async () => {
      if (!navigatedWebhookLog) {
        setLoading(true);
      }

      try {
        const webhookLog = await fetchWebhookLogDetail(id);

        if (!isActive) {
          return;
        }

        if (webhookLog?.status === 200) {
          setSelectedWebhookLog(webhookLog?.data?.data);
        } else {
          navigate(`/settings/webhooks/${key}`);
          dispatch(failure(webhookLog?.data?.message || 'Internal Server Error'));
        }
      } catch (error) {
        if (isActive) {
          navigate(`/settings/webhooks/${key}`);
          dispatch(failure(error?.data?.message || 'Internal Server Error'));
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    window.scrollTo({ top: 0, behavior: 'auto' });
    fetchWebhookLog();

    return () => {
      isActive = false;
    };
  }, [dispatch, id, key, navigate]);

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper webhookLogPageWrapper">
        <Box className="olcScreenCard webhookLogScreenCard">
          <Box className="webhookLogHeader">
            <OlcPageBackButton onClick={() => navigate(`/settings/webhooks/${key}`)}>
              {MESSAGES.SETTINGS.WEBHOOKS.LOGS.BACK_BUTTON}
            </OlcPageBackButton>
          </Box>

          {loading ? (
            <Box className="webhookLogLoading" role="status" aria-label="Loading webhook log">
              <Loader />
            </Box>
          ) : (
            <>
              <Box className="webhookLogSummaryCard">
                <Typography className="webhookLogPageTitle">
                  {MESSAGES.SETTINGS.WEBHOOKS.LOGS.TITLE}
                </Typography>

                <Box component="dl" className="webhookLogSummaryFields">
                  <Box className="webhookLogSummaryField">
                    <Typography component="dt">
                      {MESSAGES.SETTINGS.WEBHOOKS.LOGS.EVENT_TITLE}
                    </Typography>
                    <Typography component="dd">{eventTitle}</Typography>
                  </Box>
                  <Box className="webhookLogSummaryField">
                    <Typography component="dt">
                      {MESSAGES.SETTINGS.WEBHOOKS.LOGS.STATUS_TITLE}
                    </Typography>
                    <Typography component="dd">{statusCode}</Typography>
                  </Box>
                  <Box className="webhookLogSummaryField">
                    <Typography component="dt">
                      {MESSAGES.SETTINGS.WEBHOOKS.LOGS.DATE_TITLE}
                    </Typography>
                    <Typography component="dd">{formattedDate}</Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="webhookLogPayloads">
                {requestJson !== undefined && requestJson !== null && (
                  <Box className="webhookLogPayloadCard">
                    <JsonRenderer
                      title={MESSAGES.SETTINGS.WEBHOOKS.LOGS.REQUEST_TITLE}
                      json={requestJson}
                      variant="flat"
                    />
                  </Box>
                )}
                {responseJson !== undefined && responseJson !== null && (
                  <Box className="webhookLogPayloadCard">
                    <JsonRenderer
                      title={MESSAGES.SETTINGS.WEBHOOKS.LOGS.RESPONSE_TITLE}
                      json={responseJson}
                      variant="flat"
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default WebhookLog;
